import { useEffect, useState, useMemo } from "react";

import type { Dayjs } from "dayjs";

import { Drawer } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Badge } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { BrandMentionsSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/BrandMentionsSection";
import { CitationsSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/CitationsSection";
import { useDatePickerPromptReportSidePanelNavigator } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/hooks/useDatePickerPromptReportSidePanelNavigator";
import { PromptResponseSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/PromptResponseSection";
import { WebSearchQueriesSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/WebSearchQueriesSection";
import { usePromptsSidePanel } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/hooks/usePrompts";
import { getSentimentStatusConfig } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { getBaseWidgetRequestParams } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    IAiTrackerCampaign,
    ICampaignConfig,
    IIntent,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { PromptCard } from "app/pages/digital-marketing/generative-ai/shared/PromptCard";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";

import type { Prompt, IPromptsSidePanelParams } from "./types";

interface ICitationsSidePanelProps {
    isOpen: boolean;
    campaign: IAiTrackerCampaign;
    campaignConfig: ICampaignConfig;
    reportDuration: DatesRange;
    rowData: Prompt;
    onClose: () => void;
}

export const PromptReportSidePanel = ({
    isOpen,
    campaign,
    campaignConfig,
    reportDuration,
    rowData,
    onClose,
}: ICitationsSidePanelProps) => {
    const [currentDate, setCurrentDate] = useState<Dayjs>(reportDuration.to);
    const apiParams = useMemo<IPromptsSidePanelParams>(
        () => ({
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
            promptId: rowData?.promptId ?? "",
        }),
        [campaign, campaignConfig, reportDuration, rowData],
    );

    useEffect(() => {
        setCurrentDate(reportDuration.to);
    }, [reportDuration.to]);

    const { data, isLoading } = usePromptsSidePanel(apiParams, {
        enabled: isOpen && !!rowData?.promptId && !!campaign?.id,
    });

    const { currentDateData, DatePicker } = useDatePickerPromptReportSidePanelNavigator({
        data: data?.responses,
        currentDate,
        onDateChange: setCurrentDate,
    });

    const sentimentStatus = currentDateData?.mentionSentimentStatus ?? rowData?.mentionSentimentStatus;
    const sentimentConfig = getSentimentStatusConfig()?.[sentimentStatus] as IIntent;
    const citationsData =
        currentDateData?.citations?.map((citation) => ({
            icon: citation.icon,
            source: citation.domain,
            title: citation.title,
            description: citation.description,
            url: citation.link,
        })) ?? [];

    return (
        <Drawer
            isOpen={isOpen}
            isLoading={isLoading}
            onOpenChange={(state) => !state && onClose()}
            size="large"
            shouldCloseOnInteractOutside={(el) => !el.closest(".DropdownButton-popup-container")}
        >
            <Drawer.Header
                title={translate("llm.prompts.citations_side_panel.title")}
            />
            <Drawer.SubHeader>
                <Box
                    alignItems="center"
                    margin="8px 0 8px 8px"
                >
                    <DatePicker />
                </Box>
            </Drawer.SubHeader>
            <Drawer.Content>
                <Flex
                    flexDirection="column"
                    overflowY="auto"
                    height="100%"
                    width="100%"
                    gap="15px"
                >
                    {currentDateData ? (
                        <Stack
                            width="100%"
                            flex="1"
                            overflow="auto"
                        >
                            <Text
                                fontVariant="subtitle2"
                                color={colorsPalettes.carbon[500]}
                            >
                                {translate("llm.prompts.citations_side_panel.prompt.title")}
                            </Text>
                            <Box margin="8px 0 0 0">
                                <PromptCard
                                    borderless
                                    text={data.prompt}
                                    textFooterElements={[
                                        <Badge
                                            key="sentiment"
                                            bgColor={sentimentConfig?.color}
                                            color={sentimentConfig?.textColor}
                                            type="custom"
                                        >
                                            {sentimentConfig?.text || sentimentStatus}
                                        </Badge>,
                                        <Badge
                                            key="topic"
                                            bgColor={colorsPalettes.carbon[25]}
                                            color={colorsPalettes.carbon[400]}
                                            type="custom"
                                        >
                                            {currentDateData.topics?.[0] ?? rowData.topics?.[0]}
                                        </Badge>,
                                    ]}
                                >
                                    <PromptResponseSection
                                        campaign={campaign}
                                        responseText={currentDateData.responseText}
                                    />
                                    <BrandMentionsSection brands={currentDateData?.brands ?? campaign.brands} />
                                    <WebSearchQueriesSection queries={currentDateData?.webSearchQueries ?? []} />
                                    <CitationsSection
                                        citations={citationsData}
                                        defaultExpanded
                                    />
                                </PromptCard>
                            </Box>
                        </Stack>
                    ) : null}
                </Flex>
            </Drawer.Content>
        </Drawer>
    );
};
